import { inject, Injectable } from '@angular/core';

import { combineLatest } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';

import type { CustomBillingPlan } from '@core/billing-types';
import { BillingPlansDataService, getQuantityForCompanySize } from '@recruitee/billing-logic';

import { getAssignedProduct } from '../helpers/get-assigned-product';
import { getProductSafeSlotsCount } from '../helpers/get-product-safe-slots-count';
import { isPp24 } from '../helpers/is-pp24';
import { ready } from '../helpers/rx-operators';
import {
  PricingFeatureFacade,
  PricingFeatureObservableModel,
} from '../types/pricing-feature.types';

@Injectable({
  providedIn: 'root',
})
export class AssignedProductService {
  private readonly billingPlansData = inject(BillingPlansDataService);
  public data$ = combineLatest({
    model: this.model$.pipe(ready),
    billingPlans: this.billingPlansData.collection$,
    activePlan: this.billingPlansData.activePlan$,
  }).pipe(
    map(({ model, billingPlans, activePlan }) => ({
      product: getAssignedProduct(model, billingPlans, activePlan),
      model,
    })),
    tap(({ product, model }) => {
      this.product = product;
      this.productSlots = product ? getProductSafeSlotsCount(product, model) : undefined;
    }),
    map(({ product }) => product),
    shareReplay(1),
  );

  private product: CustomBillingPlan | undefined = undefined;
  private productSlots: number | undefined;

  constructor(
    private facade: PricingFeatureFacade,
    private model$: PricingFeatureObservableModel,
  ) {}

  public goToBilling() {
    if (!this.product) return;
    const { id, tier, companySize, billingPeriod } = this.product;

    if (isPp24(tier)) {
      if (!companySize) throw new Error(`companySize is missing from the custom plan id:${id}`);
      const quantity = getQuantityForCompanySize(companySize);
      this.facade.openBilling2024(id, quantity, billingPeriod);
    } else {
      this.productSlots && this.facade.openBilling(id, this.productSlots, billingPeriod);
    }
  }
}
